import {
  EmptyState,
  FlexBox,
  PageWithActions,
} from '@airship-platform/jam-web/components';
import { LeftArrowIcon } from '@airship-platform/jam-web/icons';
import Button from '@mui/material/Button';
import { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useAppointmentProposalFiles } from '../../data-access/appointmentProposalFiles';
import { useAppointmentProposalOptions } from '../../data-access/appointmentProposalOptions';
import {
  AppointmentProposal,
  useAppointmentProposals,
} from '../../data-access/appointmentProposals';
import { useAppointmentProposalUpgrades } from '../../data-access/appointmentProposalUpgrades';
import { usePricebookUniversalUpgradeProducts } from '../../data-access/pricebook-universal-upgrades-product';
import ROUTE_PATHS, { NavigationPath } from '../../routes/paths';
import ActionsSection from './ActionsSection';
import ToolkitSection from './ToolkitSection';
import { generateToolkitItems } from './utils/proposalNavigation.utils';

const ProposalNavigationPage: React.FC = () => {
  const { appointmentId, appointmentProposalId } = useParams<{
    appointmentId: string;
    appointmentProposalId: string;
  }>();
  const navigate = useNavigate();
  const { files } = useAppointmentProposalFiles();
  const { options } = useAppointmentProposalOptions();
  const { currentProposal, fetchAppointmentProposal } =
    useAppointmentProposals();
  const { pricebookUniversalUpgradeProducts } =
    usePricebookUniversalUpgradeProducts();
  const { proposalUpgrades } = useAppointmentProposalUpgrades();

  useEffect(() => {
    if (appointmentId && appointmentProposalId) {
      fetchAppointmentProposal(appointmentId, appointmentProposalId);
    }
  }, [appointmentId, appointmentProposalId, fetchAppointmentProposal]);

  if (!currentProposal) {
    return (
      <EmptyState
        title="Appointment not found"
        subtitle="Oops! The requested appointment was not found."
      />
    );
  }

  const buildNavigatePath = (
    proposal: AppointmentProposal,
    path: NavigationPath,
    searchParams?: Record<string, string>
  ) =>
    ROUTE_PATHS.APPOINTMENT_PATH({
      appointmentId: proposal.appointmentId,
      appointmentProposalId: proposal.appointmentProposalId,
      path,
      searchParams,
    });

  const navigateTo =
    (proposal: AppointmentProposal) =>
    (path: NavigationPath, searchParams?: Record<string, string>) =>
      navigate(buildNavigatePath(proposal, path, searchParams));

  return (
    <PageWithActions
      actionsComponent={
        <FlexBox
          alignItems={'center'}
          justifyContent={'space-between'}
          width={'100%'}
        >
          <Button
            color={'softGold'}
            startIcon={<LeftArrowIcon />}
            component={Link}
            to={buildNavigatePath(currentProposal, 'options')}
          >
            Back
          </Button>
        </FlexBox>
      }
      sx={{ alignItems: 'center' }}
    >
      <FlexBox flexDirection={'column'} gap={3} width={'100%'} maxWidth={'md'}>
        <ToolkitSection
          navItems={generateToolkitItems(
            navigateTo(currentProposal),
            currentProposal,
            files,
            options,
            pricebookUniversalUpgradeProducts,
            proposalUpgrades
          )}
        />
        <ActionsSection proposal={currentProposal} />
      </FlexBox>
    </PageWithActions>
  );
};

export default ProposalNavigationPage;
